import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { Category, Subcategory, QueryParams } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
// אייקונים
import { 
  faPlus, 
  faEdit, 
  faTrash,
  faSave,
  faBox
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

import { buildApiUrl } from '../config/api';

const CategoryList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showCategoryForm, setShowCategoryForm] = useState<boolean>(false);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [newCategoryName, setNewCategoryName] = useState<string>('');

  const [showSubcategoryForm, setShowSubcategoryForm] = useState<boolean>(false);
  const [subcategoryToEdit, setSubcategoryToEdit] = useState<Subcategory | null>(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState<string>('');
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState<number | ''>('');
  
  const [searchQuery, setSearchQuery] = useState<string>('');
  // הוסר sortBy כי לא נעשה בו שימוש
  
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');
  
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: QueryParams = {};
      if (searchQuery) params.search = searchQuery;

      const [catRes, subcatRes] = await Promise.all([
          axios.get(buildApiUrl('settings/categories/'), { params }),
          axios.get(buildApiUrl('settings/subcategories/'))
      ]);
      setCategories(catRes.data);
      setSubcategories(subcatRes.data);
    } catch (err) {
      console.error("שגיאה בטעינת קטגוריות:", err);
      setError("לא ניתן לטעון קטגוריות.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCategories();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]); // --- תיקון: הוספת תלות ל-searchQuery ---
  
  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
      setMessageModalTitle(title);
      setMessageModalContent(message);
      setMessageModalVariant(variant);
      setShowMessageModal(true);
  };

  const handleAddCategoryClick = () => {
      setCategoryToEdit(null);
      setNewCategoryName('');
      setShowCategoryForm(true);
  };

  const handleEditCategoryClick = (category: Category) => {
      setCategoryToEdit(category);
      setNewCategoryName(category.category_name);
      setShowCategoryForm(true);
  };
  
  const handleCategoryFormSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      try {
      if (categoryToEdit) {
          await axios.put(buildApiUrl(`settings/categories/${categoryToEdit.id}/`), { category_name: newCategoryName });
          showMessage('הצלחה', `הקטגוריה "${categoryToEdit.category_name}" עודכנה ל-"${newCategoryName}" בהצלחה!`, 'success');
      } else {
          await axios.post(buildApiUrl('settings/categories/'), { category_name: newCategoryName });
          showMessage('הצלחה', `הקטגוריה "${newCategoryName}" נוצרה בהצלחה!`, 'success');
      }
          setShowCategoryForm(false);
          fetchCategories();
      } catch (err) {
          console.error("שגיאה בשמירת קטגוריה:", err);
          showMessage('שגיאה', 'שגיאה בשמירת הקטגוריה. אנא נסה שוב.', 'danger');
      } finally {
          setLoading(false);
      }
  };

  const handleDeleteCategoryClick = (id: number) => {
      setConfirmModalTitle('אישור מחיקה');
      setConfirmModalMessage('האם אתה בטוח שברצונך למחוק קטגוריה זו? כל תת-הקטגוריות והציוד המשויך יאבדו את הקטגוריה.');
      setConfirmAction(() => async () => {
        try {
          const cat = categories.find(c => c.id === id);
          await axios.delete(buildApiUrl(`settings/categories/${id}/`));
          showMessage('הצלחה', `הקטגוריה ${cat ? `"${cat.category_name}" ` : ''}נמחקה בהצלחה!`, 'success');
          fetchCategories();
        } catch (err) {
          showMessage('שגיאה', 'שגיאה במחיקת הקטגוריה. אנא נסה שוב.', 'danger');
        }
      });
      setShowConfirmModal(true);
  };

  const handleAddSubcategoryClick = () => {
      setSubcategoryToEdit(null);
      setNewSubcategoryName('');
      setSelectedCategoryForSub('');
      setShowSubcategoryForm(true);
  };

  const handleEditSubcategoryClick = (sub: Subcategory) => {
      setSubcategoryToEdit(sub);
      setNewSubcategoryName(sub.subcategory_name);
      setSelectedCategoryForSub(sub.category);
      setShowSubcategoryForm(true);
  };

  const handleSubcategoryFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (subcategoryToEdit && subcategoryToEdit.id) {
        await axios.put(buildApiUrl(`settings/subcategories/${subcategoryToEdit.id}/`), { subcategory_name: newSubcategoryName, category: selectedCategoryForSub });
        showMessage('הצלחה', `תת-הקטגוריה "${subcategoryToEdit.subcategory_name}" עודכנה ל-"${newSubcategoryName}" בהצלחה!`, 'success');
      } else {
        await axios.post(buildApiUrl('settings/subcategories/'), { subcategory_name: newSubcategoryName, category: selectedCategoryForSub });
        showMessage('הצלחה', `תת-הקטגוריה "${newSubcategoryName}" נוצרה בהצלחה!`, 'success');
      }
      setShowSubcategoryForm(false);
      fetchCategories();
    } catch (err) {
      console.error("שגיאה בשמירת תת-קטגוריה:", err);
      showMessage('שגיאה', 'שגיאה בשמירת התת-קטגוריה. אנא נסה שוב.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategoryClick = (id: number) => {
      setConfirmModalTitle('אישור מחיקה');
      setConfirmModalMessage('האם אתה בטוח שברצונך למחוק תת-קטגוריה זו? הציוד המשויך יאבד את התת-קטגוריה.');
      setConfirmAction(() => async () => {
        try {
          const sub = subcategories.find(s => s.id === id);
          await axios.delete(buildApiUrl(`settings/subcategories/${id}/`));
          showMessage('הצלחה', `תת-הקטגוריה ${sub ? `"${sub.subcategory_name}" ` : ''}נמחקה בהצלחה!`, 'success');
          fetchCategories();
        } catch (err) {
          showMessage('שגיאה', 'שגיאה במחיקת התת-קטגוריה. אנא נסה שוב.', 'danger');
        }
      });
      setShowConfirmModal(true);
  };


  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>ניהול קטגוריות ותת-קטגוריות</h2>
        <div>
          <Button variant="info" onClick={() => navigate('/equipment')} className="me-2">
            <FontAwesomeIcon icon={faBox} className="me-1" />
            ניהול ציוד
          </Button>
                    <Button variant="success" onClick={handleAddCategoryClick} className="me-2">
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף קטגוריה
        </Button>
        <Button variant="primary" onClick={handleAddSubcategoryClick}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף תת-קטגוריה
        </Button>
        </div>
      </div>

      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={12}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control type="text" placeholder="חפש לפי שם קטגוריה או תת-קטגוריה..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        (!categories || categories.length === 0) ? (
          <p>אין קטגוריות במערכת.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>שם קטגוריה</th>
                <th>תת-קטגוריות</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id}>
                  <td>
                    <strong>{cat.category_name}</strong>
                  </td>
                  <td>
                    <ul className="list-unstyled mb-0">
                      {subcategories.filter(sub => sub.category === cat.id).map(sub => (
                        <li key={sub.id} className="d-flex justify-content-between align-items-center my-1">
                          <span>{sub.subcategory_name}</span>
                          <div>
                                <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditSubcategoryClick(sub)}>
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  ערוך
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteSubcategoryClick(sub.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                                  מחק
                                </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                                <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditCategoryClick(cat)}>
                                  <FontAwesomeIcon icon={faEdit} className="me-1" />
                                  ערוך
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteCategoryClick(cat.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                                  מחק
                                </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      <Modal show={showCategoryForm} onHide={() => setShowCategoryForm(false)}>
          <Modal.Header closeButton><Modal.Title>{categoryToEdit ? 'עריכת קטגוריה' : 'הוספת קטגוריה חדשה'}</Modal.Title></Modal.Header>
          <Modal.Body>
              <Form onSubmit={handleCategoryFormSubmit}>
                  <Form.Group className="mb-3" controlId="formCategoryName">
                      <Form.Label>שם קטגוריה</Form.Label>
                      <Form.Control type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} required />
                  </Form.Group>
                  <Button variant="primary" type="submit" disabled={loading}>{loading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> שמור</>}</Button>
              </Form>
          </Modal.Body>
      </Modal>

      <Modal show={showSubcategoryForm} onHide={() => setShowSubcategoryForm(false)}>
          <Modal.Header closeButton><Modal.Title>{subcategoryToEdit ? 'עריכת תת-קטגוריה' : 'הוספת תת-קטגוריה חדשה'}</Modal.Title></Modal.Header>
          <Modal.Body>
              <Form onSubmit={handleSubcategoryFormSubmit}>
                  <Form.Group className="mb-3" controlId="formSubcategoryName">
                      <Form.Label>שם תת-קטגוריה</Form.Label>
                      <Form.Control type="text" value={newSubcategoryName} onChange={(e) => setNewSubcategoryName(e.target.value)} required />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="formSubcategoryCategory">
                      <Form.Label>קטגוריה ראשית</Form.Label>
                      <Form.Select value={selectedCategoryForSub} onChange={(e) => setSelectedCategoryForSub(parseInt(e.target.value) || '')} required>
                          <option value="">בחר קטגוריה...</option>
                          {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.category_name}</option>)}
                      </Form.Select>
                  </Form.Group>
                  <Button variant="primary" type="submit" disabled={loading}>{loading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faSave} className="me-1" /> שמור</>}</Button>
              </Form>
          </Modal.Body>
      </Modal>

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
    </div>
  );
};

export default CategoryList;
